import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { addMedicineRequestApi } from "../../../services/medicine-tracking.service";
import { useGetApi } from "../../../hooks/useGetApi";
import { getItemApi } from "../../../services/item.service";
import { useNavigate } from "react-router-dom";

const MedicineRequestModal = ({ open, onClose, selectedRow }) => {
  const navigate = useNavigate();
  const initialState = {
    patientId: selectedRow?.occupancyDetails?.patientId || "",
    visitId: selectedRow?.occupancyDetails?.visitId || "",
    wardId: selectedRow?.wardId || "",
    bedNumberId: selectedRow?.bedNumberId || "",
    medicines: [
      {
        id: "1",
        medicineId: null,
        requestedQuantity: 0,
        priority: "",
      },
    ],
  };

  const initialMaxId = Math.max(
    ...initialState.medicines?.map((s) => parseInt(s?.id, 10)),
    0
  );
  const nextIdRef = useRef(initialMaxId + 1);

  const [formData, setFormData] = useState(initialState);
  const [loading, setLoading] = useState(false);

  // selectedRow can arrive/complete after the modal mounts.
  // Keep formData synced so submit uses the correct patientId/visitId.
  useEffect(() => {
    if (!open) return;
    nextIdRef.current = initialMaxId + 1;
    setFormData(initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedRow?._id]);

  const { dataList: medicineList } = useGetApi({
    apiFunction: getItemApi,
    params: { limit: Number.MAX_SAFE_INTEGER, type: "Medicine" },
    dataKey: "items",
  });

  const addMedicinesDetail = () => {
    const newId = nextIdRef.current.toString();
    nextIdRef.current += 1;

    const newMedicine = {
      id: newId,
      medicineId: "",
      requestedQuantity: 0,
      priority: "",
    };

    setFormData((prev) => ({
      ...prev,
      medicines: [...prev.medicines, newMedicine]
    }));
  };

  const removeMedicinesDetail = (medicineId) => {
    setFormData((prev) => ({
      ...prev,
      medicines: prev.medicines?.filter((s) => s?.id !== medicineId)
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const [field, medId] = name.split("-");

    setFormData((prev) => ({
      ...prev,
      medicines: prev.medicines.map((med) =>
        med.id === medId
          ? {
              ...med,
              [field]: field === "requestedQuantity" ? Number(value) : value,
            }
          : med
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isDev = process.env.NODE_ENV === "development";
    if (isDev) {
      console.log("[MedicineRequestModal] === FORM SUBMISSION START ===");
      console.log(
        "[MedicineRequestModal] selectedRow:",
        JSON.stringify(selectedRow, null, 2)
      );
      console.log(
        "[MedicineRequestModal] Form data:",
        JSON.stringify(formData, null, 2)
      );
    }
    
    try {
        // Validate form data
        if (isDev) console.log("[MedicineRequestModal] Validating form data...");
        
        if (!formData.patientId || !formData.visitId) {
            console.error('[MedicineRequestModal] Validation failed: Missing patientId or visitId');
            toast.error('Patient and visit information is required');
            return;
        }
        
        if (!formData.medicines || formData.medicines.length === 0) {
            console.error('[MedicineRequestModal] Validation failed: No medicines added');
            toast.error('Please add at least one medicine');
            return;
        }
        
        // Validate each medicine
        for (let i = 0; i < formData.medicines.length; i++) {
            const medicine = formData.medicines[i];
            if (isDev) console.log(`[MedicineRequestModal] Validating medicine ${i + 1}:`, medicine);
            
            if (!medicine.medicineId) {
                console.error(`[MedicineRequestModal] Validation failed: Medicine ${i + 1} missing medicineId`);
                toast.error(`Medicine ${i + 1}: Please select a medicine`);
                return;
            }
            
            if (!medicine.requestedQuantity || medicine.requestedQuantity <= 0) {
                console.error(`[MedicineRequestModal] Validation failed: Medicine ${i + 1} invalid quantity`);
                toast.error(`Medicine ${i + 1}: Please enter a valid quantity`);
                return;
            }
        }
        
        if (isDev) console.log("[MedicineRequestModal] All validations passed, preparing request body...");
        
        // Prepare request body
        const requestBody = {
            patientId: formData.patientId,
            visitId: formData.visitId,
            wardId: formData.wardId,
            bedNumberId: formData.bedNumberId,
            medicines: formData.medicines.map(med => ({
                // Autocomplete stores the selected option object; backend expects a scalar ObjectId string
                medicineId: med.medicineId?._id || med.medicineId,
                requestedQuantity: parseInt(med.requestedQuantity),
                priority: med.priority || 'Normal'
            })),
            notes: formData.notes
        };
        
        if (isDev) {
          console.log(
            "[MedicineRequestModal] Request body prepared:",
            JSON.stringify(requestBody, null, 2)
          );
          console.log("[MedicineRequestModal] Checking required fields:");
          console.log("  - patientId:", requestBody.patientId);
          console.log("  - visitId:", requestBody.visitId);
          console.log("  - wardId:", requestBody.wardId);
          console.log("  - bedNumberId:", requestBody.bedNumberId);
          console.log("[MedicineRequestModal] Calling API...");
        }
        const response = await addMedicineRequestApi(requestBody);
        if (isDev) {
          console.log(
            "[MedicineRequestModal] API response received:",
            JSON.stringify(response, null, 2)
          );
        }
        
        if (response && response.success) {
            if (isDev) console.log("[MedicineRequestModal] Request successful, showing success message");
            toast.success('Medicine request submitted successfully');
            onClose();
        } else {
            console.error('[MedicineRequestModal] API returned error:', response);
            toast.error(response?.message || 'Failed to submit medicine request');
        }
        if (isDev) console.log("[MedicineRequestModal] === FORM SUBMISSION END ===");
        
    } catch (error) {
        console.error('[MedicineRequestModal] ERROR during submission:', error);
        toast.error('Failed to submit medicine request');
        if (isDev) console.log("[MedicineRequestModal] === FORM SUBMISSION END (ERROR) ===");
    }
};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            maxWidth: "unset",
            width: { xs: "95vw", lg: "1100px" },
            bgcolor: "#E5F4FF",
            borderRadius: "20px",
          },
        },
      }}
    >
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: "20px",
          }}
        >
          <Typography variant="h6" sx={{ color: "#5D6A82" }}>
            Medication Request Details
          </Typography>
          <Close
            onClick={onClose}
            sx={{ cursor: "pointer", color: "#5D6A82" }}
          />
        </Box>
        <Box component="form" onSubmit={handleSubmit}>
          {formData?.medicines?.map((medicine, index) => (
            <Grid
              container
              spacing={3}
              key={medicine?.id}
              sx={{
                mb: index !== formData?.medicines?.length - 1 ? "40px" : "20px",
              }}
            >
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={medicineList || []}
                  getOptionLabel={(option) => option?.itemName || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Medication Name"
                      required
                      fullWidth
                    />
                  )}
                  value={medicine?.medicineId || null}
                  onChange={(_, newValue) =>
                    handleChange({
                      target: {
                        name: `medicineId-${medicine.id}`,
                        value: newValue,
                      },
                    })
                  }
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  type="number"
                  label="Quantity"
                  name={`requestedQuantity-${medicine.id}`}
                  value={medicine?.requestedQuantity || ""}
                  onChange={handleChange}
                  sx={{ bgcolor: "white" }}
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={["Normal", "High", "Urgent"]}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Priority Level"
                      required
                      fullWidth
                    />
                  )}
                  value={medicine?.priority || null}
                  onChange={(_, newValue) =>
                    handleChange({
                      target: {
                        name: `priority-${medicine.id}`,
                        value: newValue,
                      },
                    })
                  }
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              {formData?.medicines?.length > 1 && index !== 0 && (
                <Box sx={{ textAlign: "right", width: "100%" }}>
                  <Button
                    variant="text"
                    onClick={() => removeMedicinesDetail(medicine?.id)}
                    color="error"
                    sx={{ ml: "25px", mt: "5px", textTransform: "none" }}
                  >
                    Remove
                  </Button>
                </Box>
              )}
            </Grid>
          ))}

          <Button
            onClick={addMedicinesDetail}
            variant="text"
            sx={{ color: "#92A6CC", textTransform: "none" }}
          >
            + Add Medicine Request
          </Button>

          <Box sx={{ my: "20px", textAlign: "center" }}>
            <Button
              type="submit"
              variant="contained"
              sx={{ width: "150px", textTransform: "none" }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Submit"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MedicineRequestModal;
